<template>
	<div class="user-speaker-page page">
		<v-skeleton-loader v-if="this.isLoading" v-bind="attrs" type="card" />
		<div v-else-if="!this.isSpeakerEmpty">
			<v-card class="user-speaker-page__info-wrapper" flat>
				<div class="user-speaker-page__avatar-wrapper">
					<Avatar :avatar="avatarPath" :size="120" border-radius="square" class="mr-6" />
				</div>
				<div class="user-speaker-page__info">
					<h1 class="user-speaker-page__info-username">
						{{ userName }}
					</h1>
					<p v-if="companyName" class="user-speaker-page__info-description">
						{{ companyName }}
					</p>
					<p v-if="heldPosition" class="user-speaker-page__info-description">
						{{ heldPosition }}
					</p>
					<a
						v-if="website"
						class="user-speaker-page__info-website"
						:href="website"
						target="_blank"
					>
						<Icon
							type="link"
							:size="16"
							color="var(--button-secondary-background-color)"
							:pr="6"
						/>
						<span>
							{{ website }}
						</span>
					</a>
				</div>
			</v-card>
			<!-- Speaker sessions -->
			<div class="user-speaker-page__sessions-container mb-2">
				<div class="user-speaker-page__sessions-container__session d-flex justify-center">
					<router-link
						:to="{
							name: 'speaker-sessions',
							params: { id: speakerId, type: 'future' },
						}"
						class="user-speaker-page__sessions d-flex align-center flex-column justify-center"
					>
						<Icon
							class="mb-2"
							type="remove_schedule"
							color="var(--accent-color)"
							:size="24"
						/>
						<span class="user-speaker-page__sessions-label"> Active Sessions </span>
					</router-link>
				</div>

				<div class="user-speaker-page__sessions-container__session d-flex justify-center">
					<router-link
						:to="{
							name: 'speaker-sessions',
							params: { id: speakerId, type: 'past' },
						}"
						class="user-speaker-page__sessions d-flex align-center flex-column justify-center"
					>
						<Icon
							class="mb-2"
							type="add_schedule"
							color="var(--accent-color)"
							:size="24"
						/>
						<span class="user-speaker-page__sessions-label"> Past Sessions </span>
					</router-link>
				</div>
			</div>

			<!-- Speaker contacts -->

			<v-card
				v-if="twitterValue || linkedinValue || facebookValue || instagramValue"
				class="user-speaker-page__section"
				flat
			>
				<h3 class="user-speaker-page__section-title">Social Networks</h3>
				<div v-if="twitterValue" class="user-speaker-page__network-wrapper">
					<h4 class="user-speaker-page__network-title">Twitter</h4>
					<a :href="twitterValue" target="_blank" class="user-speaker-page__network">
						<Icon color="var(--accent-color)" type="twitter" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ twitterValue }}
						</span>
					</a>
				</div>
				<div v-if="linkedinValue" class="user-speaker-page__network-wrapper">
					<h4 class="user-speaker-page__network-title">LinkedIn</h4>
					<a :href="linkedinValue" target="_blank" class="user-speaker-page__network">
						<Icon color="var(--accent-color)" type="linkedin" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ linkedinValue }}
						</span>
					</a>
				</div>
				<div v-if="facebookValue" class="user-speaker-page__network-wrapper">
					<h4 class="user-speaker-page__network-title">Facebook</h4>
					<a :href="facebookValue" target="_blank" class="user-speaker-page__network">
						<Icon color="var(--accent-color)" type="facebook" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ facebookValue }}
						</span>
					</a>
				</div>
				<div v-if="instagramValue" class="user-speaker-page__network-wrapper">
					<h4 class="user-speaker-page__network-title">Instagram</h4>
					<a :href="instagramValue" target="_blank" class="user-speaker-page__network">
						<Icon color="var(--accent-color)" type="instagram" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ instagramValue }}
						</span>
					</a>
				</div>
			</v-card>

			<v-card v-if="emailValue || phoneValue" class="user-speaker-page__section" flat>
				<h3 class="user-speaker-page__section-title">Contacts</h3>
				<div class="mb-3" v-if="phoneValue">
					<h4 class="user-speaker-page__network-title">Phone</h4>
					<div class="user-speaker-page__contact-box d-flex">
						<Icon color="var(--accent-color)" type="phone" :size="24" :pr="8" />
						<span class="user-speaker-page__phone">
							{{ phoneValue }}
						</span>
					</div>
				</div>
				<div v-if="emailValue">
					<h4 class="user-speaker-page__network-title">Email</h4>
					<div class="user-speaker-page__contact-box d-flex">
						<Icon color="var(--accent-color)" type="email" :size="25" :pr="8" />
						<span class="user-speaker-page__email">
							{{ emailValue }}
						</span>
					</div>
				</div>
			</v-card>

			<v-card v-if="bio" class="user-speaker-page__section" flat>
				<h3 class="user-speaker-page__section-title">Speaker Bio</h3>
				<p class="user-speaker-page__bio" v-html="bio"></p>
			</v-card>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import Avatar from '@/components/Avatar'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'SpeakerPage',
	mixins: [Dwh],
	components: {
		Avatar,
	},
	computed: {
		...mapGetters('user', ['isLoading', 'speaker']),
		...mapGetters('event', ['event']),
		isSpeakerEmpty() {
			const { speaker_id: speakerId } = this.speaker || {}

			return speakerId !== +this.$route.params.id
		},

		speakerId() {
			const { speaker_id: speakerId } = this.speaker || {}

			return speakerId
		},

		sessionsCountPast() {
			const { metaList: { past: { totalCount: pastSessions } = {} } = {} } =
				this.speaker || {}

			return pastSessions || 0
		},
		sessionsCountFuture() {
			const { metaList: { future: { totalCount: futureSessions } = {} } = {} } =
				this.speawebsiteker || {}

			return futureSessions || 0
		},

		userName() {
			const { speaker_name: name } = this.speaker || {}
			return name || ''
		},
		avatarPath() {
			const { speaker_logo: userLogo } = this.speaker || {}
			return userLogo
		},
		heldPosition() {
			const { speaker_title: heldPosition } = this.speaker || {}
			return heldPosition
		},
		companyName() {
			const { speaker_company: company } = this.speaker || {}
			return company
		},
		website() {
			const { speaker_website: website } = this.speaker || {}
			return website
		},
		bio() {
			const { speaker_bio: bio } = this.speaker || {}
			return bio
		},
		twitterValue() {
			const { speaker_twitter_url: social = {} } = this.speaker || {}
			return social || ''
		},
		linkedinValue() {
			const { speaker_linked_in: social = {} } = this.speaker || {}
			return social || ''
		},
		facebookValue() {
			const { speaker_facebook: social = {} } = this.speaker || {}
			return social || ''
		},
		instagramValue() {
			const { speaker_instagram: social = {} } = this.speaker || {}
			return social || ''
		},
		phoneValue() {
			const { speaker_phone: phone = {} } = this.speaker || {}
			return phone || ''
		},
		emailValue() {
			const { speaker_email: email = {} } = this.speaker || {}
			return email || ''
		},
	},
	methods: {
		...mapActions('user', ['getSpeaker', 'getSpeakerSessions']),
		...mapActions('app', ['setPageTitle']),

		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
	},
	data() {
		return {
			searchIcon,
			successMessage: false,
			errorMessage: false,
			attrs: {
				elevation: 0,
			},
			socialFieldNames: [
				'user_linkedin',
				'speaker_facebook',
				'user_twitter',
				'user_custom_url',
				'speaker_instagram',
				'speaker_website',
				'speaker_company',
				'speaker_logo',
			],
			phoneFieldNames: ['speaker_phone', 'speaker_email'],
		}
	},
	created() {
		const speaker = this.speaker
		const id = +this.$route.params.id
		if (id && ((speaker && speaker.speaker_id !== id) || !speaker)) {
			this.getSpeaker(id)
			if (!speaker || !speaker.sessions || (speaker && speaker.speaker_id !== id))
				this.getSpeakerSessions({ id })
		}
		this.sendTrigger({
			category: 'Content',
			action: 'open_speaker_profile_event',
			module: 'Events',
			data: id,
			content_chapter_id:
				this.event && this.event.chapter_id ? [this.event.chapter_id] : null,
		})
	},
	mounted() {
		this.setPageTitle('Speaker')
	},
	beforeDestroy() {
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.user-speaker-page {
	font-family: 'Inter', sans-serif;
	&__sessions-container {
		display: flex;
		gap: 8px;
		&__session {
			width: 100%;
			height: 75px;
			display: flex;
			border: 1px solid #e2e2e2;
			background-color: #ffffff;
			border-radius: 8px;
		}
	}

	&__email {
		color: #002855;
		font-family: 'Inter', sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 17.07px;
		text-align: left;
		margin: 4px;
	}

	&__phone {
		color: #002855;
		font-family: 'Inter', sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 17.07px;
		text-align: left;
		margin: 4px;
	}

	&__section-website {
		color: #4a4a4a;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 700;
		line-height: 19.5px;
		text-align: left;
	}

	&__title {
		font-size: 24px;
		font-weight: 700;
		line-height: 29.26px;
		color: var(--company-position-large);
		margin-bottom: 20px;
	}

	&__info {
		max-width: calc(100% - 144px);
		display: flex;
		flex-direction: column;

		@media (max-width: 480px) {
			max-width: 100%;
		}

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;
			padding: 12px;
			border-radius: 8px !important;
			margin-bottom: 8px;
			border: 1px solid #e2e2e2 !important;

			@media (max-width: 480px) {
				flex-direction: column;
			}
		}

		&-username {
			font-size: 24px;
			font-weight: 700;
			line-height: 29.26px;
			color: var(--accent-color);
			margin-bottom: 12px;
			word-break: break-word;
		}

		&-description {
			font-size: 16px;
			line-height: 19.5px;
			color: var(--company-position-large);
			margin-bottom: 4px !important;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}

		&-website {
			display: flex;
			flex-wrap: nowrap;
			align-items: center;
			font-size: 13px;
			line-height: 17px;
			font-weight: 600;
			color: var(--button-main-background-color);
			margin-top: 8px;
			text-decoration: unset;

			span {
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}
	}

	&__avatar-wrapper {
		@media (max-width: 480px) {
			display: flex;
			justify-content: center;
			margin-bottom: 8px;
		}
	}

	&__section {
		align-items: center;
		padding: 12px !important;
		margin-bottom: 8px;
		border-radius: 8px !important;
		border: 1px solid #e2e2e2 !important;
	}

	&__network {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		text-decoration: none;

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: var(--company-position-large);
			margin-bottom: 4px;
		}

		&-link {
			font-size: 14px;
			line-height: 17.07px;
			color: var(--accent-color);
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}

		&-wrapper:not(:last-child) {
			margin-bottom: 12px;
		}
	}

	&__bio {
		font-size: 14px;
		line-height: 17.07px;
		color: var(--accent-color);
		margin: 0 !important;
	}

	&__sessions {
		height: 75px;
		text-decoration: none;

		&-icon {
			color: #42a0d8 !important;
			margin-right: 10px;
		}

		&-label {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color);
		}
	}
}
</style>
